import { axiosPrivate } from './axios';

/**
 * Request Service
 * Handles all request-related API calls
 */

const requestService = {
  /**
   * Create a new request
   * @param {Object} requestData - Request data
   * @returns {Promise} API response
   */
  createRequest: async (requestData) => {
    const response = await axiosPrivate.post('/api/requests', requestData);
    return response.data;
  },

  /**
   * Get requests
   * - Admins get all requests
   * - Normal users get only their requests
   * @param {Object} [params] - Optional query params: { page, limit, team, status, requestType, activeOn }
   *   Passing page/limit returns one page plus a `pagination` object; omitting
   *   them returns the full list (legacy behaviour).
   * @returns {Promise} API response
   */
  getRequests: async (params = {}) => {
    const response = await axiosPrivate.get('/api/requests', { params });
    return response.data;
  },

  /**
   * Get a single request by ID
   * @param {String} requestId - Request ID
   * @returns {Promise} API response
   */
  getRequestById: async (requestId) => {
    const response = await axiosPrivate.get(`/api/requests/${requestId}`);
    return response.data;
  },

  /**
   * Approve a request (Admin only)
   * @param {String} requestId - Request ID
   * @returns {Promise} API response
   */
  approveRequest: async (requestId) => {
    const response = await axiosPrivate.post(`/api/requests/${requestId}/approve`);
    return response.data;
  },

  /**
   * Reject a request (Admin only)
   * @param {String} requestId - Request ID
   * @returns {Promise} API response
   */
  rejectRequest: async (requestId) => {
    const response = await axiosPrivate.post(`/api/requests/${requestId}/reject`);
    return response.data;
  },

  /**
   * Generate random WFH requests (Admin only)
   * @param {Object} data - { weekStart, employeeIds, daysPerEmployee }
   * @returns {Promise} API response
   */
  generateRandomWFH: async (data) => {
    const response = await axiosPrivate.post('/api/requests/random-wfh', data);
    return response.data;
  },

  /**
   * Edit a request
   * @param {String} requestId - Request ID
   * @param {Object} updateData - Updated request data
   * @returns {Promise} API response
   */
  editRequest: async (requestId, updateData) => {
    const response = await axiosPrivate.put(`/api/requests/${requestId}/edit`, updateData);
    return response.data;
  },

  /**
   * Delete a request (Admin only)
   * @param {String} requestId - Request ID
   * @returns {Promise} API response
   */
  deleteRequest: async (requestId) => {
    const response = await axiosPrivate.delete(`/api/requests/${requestId}`);
    return response.data;
  },
};

export default requestService;
